import React, { memo, ReactNode, useCallback, useMemo, useRef, useState, CSSProperties } from 'react';
import { OverlayContainer } from 'react-aria';
import { DOMProps } from '@react-types/shared';
import { ValueOf, useHasScrollbar, useIsomorphicLayoutEffect } from '@direct-frontend/stdlib';
import { isMobile } from '@direct-frontend/platform-code';
import { useLogger, Loggable } from '@direct-frontend/modules-logger';

import { Modal, type ModalAnimationState } from '../Modal';
import { HStack, VStack } from '../Stack';
import { Island } from '../Island';
import { useOverlay } from '../../hooks/useOverlay/useOverlay';
import { LayerFocusScope } from '../../lib/layerManager/components/LayerFocusScope/LayerFocusScope';
import { getScrollbarClassName } from '../../lib/css/getScrollbarClassName';
import { TestProps } from '../../internal/types/testTypes';
import { cn } from '../../lib/css/className/className';
import { LayerContextProvider, LayerFixedPriorities, useNewLayer } from '../../lib/layerManager/layerManager';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { FloatingButton } from '../FloatingButton';
import { IconCloseSkinny } from '../../icons';

import { useOpenSidePanelController } from './hooks/useOpenSidePanelController';

import './SidePanel.css';

const cls = cn('SidePanel');

/**
 * Классы для кастомного скроллбара
 */
const scrollClasses = getScrollbarClassName();

/**
 * Ширина шторки
 */
export type SidePanelWidth = 's' | 'm' | 'l';

/**
 * Маппинг human-friendly названий ширин на пиксели
 */
const pixelWidthBySidePanelWidth: Record<SidePanelWidth, number> = {
    s: 560,
    m: 760,
    l: 960,
};

/**
 * Пропсы компонента SidePanel
 */
export interface SidePanelProps extends SidePanelContentProps, TestProps, DOMProps, Loggable {
    /**
     * Дополнительный класс для элемента
     */
    className?: string;

    /**
     * Класс для внутреннего элемента
     */
    contentClassName?: string;

    /**
     * Переключение между открытым и закрытым состояниями
     */
    isOpen: boolean;

    /**
     * Будет ли закрываться шторка при клике вне её
     */
    dismissOnOutsideClick?: boolean;

    /**
     * Признак возможности перемещения мобильной шторки (при выключении шторку нельзя двигать пальцем)
     * @default true
     */
    isDraggableMobile?: boolean;

    /**
     * Блокировать ли взаимодействие с контентом страницы за шторкой.
     * __Работает в паре с__ dismissOnOutsideClick
     * @default false
     */
    isModal?: boolean;

    /**
     * Действие, выполняемое при закрытии
     */
    onClose: () => void;

    /**
     * Ширина шторки
     * @default m
     */
    width?: SidePanelWidth;

    /**
     * Действие, выполняемое при завершении анимации
     */
    onAnimationEnd?: (animationState: SidePanelState) => void;
}

/**
 * Пропсы компонента SidePanel задающие содержимое компонента
 */
interface SidePanelContentProps {
    /**
     * Основное содержимое шторки
     * Используйте overlayContent вместо children
     * @deprecated
     */
    children?: ReactNode;

    /**
     * Основное содержимое шторки (новое отображение через overlayContent)
     */
    overlayContent?: ReactNode;

    /**
     * Содержимое нижней панели (не передавайте сюда Stack)
     * @example <><Button>Кнопка 1</Button><Button>Кнопка 2</Button></>
     */
    bottomButtons?: ReactNode;
}

/** Состояние открытости шторки */
export type SidePanelState = 'open' | 'close';

/**
 * Стейт анимации шторки
 */
const SidePanelAnimationState = {
    /**
     * Открытие шторки
     */
    OPENING: 'opening',
    /**
     * Закрытие шторки
     */
    CLOSING: 'closing',
    /**
     * Нет анимации
     */
    NONE: 'none',
} as const;
type SidePanelAnimationState = ValueOf<typeof SidePanelAnimationState>;

/**
 * Компонент выдвигающейся шторки для показа произвольного контента
 * @returns {JSX.Element}
 */
export const SidePanel = memo<SidePanelProps>(function SidePanel({
    children,
    bottomButtons,
    className,
    contentClassName,
    logNode,
    width = 'm',
    onClose,
    isOpen,
    isDraggableMobile = true,
    dismissOnOutsideClick = false,
    isModal = false,
    onAnimationEnd,
    'data-testid': dataTestId = 'SidePanel',
    overlayContent,
}) {
    const isMobileMode = isMobile();
    const { LogProvider } = useLogger('SidePanel', logNode);
    const hasBottomButtons = Boolean(bottomButtons);
    const { hasScrollbar, setRef: setContentContainerRef } = useHasScrollbar('y');

    const layerRef = useRef<HTMLDivElement>(null);
    const [addToDom, setAddToDom] = useState(false);
    const layerManager = useNewLayer({
        priority: LayerFixedPriorities.SidePanel,
        contextPopupsContainerRef: layerRef,
    });
    const { overlayProps } = useOverlay(
        {
            isOpen: isOpen,
            isLayer: true,
            isModal: isMobileMode || isModal,
            isDismissable: dismissOnOutsideClick,
            zIndex: layerManager.zIndex,
            id: layerManager.id,
            isKeyboardDismissDisabled: true,
            onClose: onClose,
        },
        layerRef,
    );
    const colorSkinClass = getColorSkin('gray-neutral', {});

    // Механизм поддерживающий открытой только одну шторку
    const onOtherInstanceOpening = useCallback(() => {
        if (isOpen) {
            onClose();
        }
    }, [isOpen, onClose]);
    const { notifyOpening } = useOpenSidePanelController({ onOtherInstanceOpening });

    // Анимации открытия и закрытия шторки
    const [animationState, setAnimation] = useState<SidePanelAnimationState>(
        isOpen ? SidePanelAnimationState.OPENING : SidePanelAnimationState.CLOSING,
    );
    if (isOpen) {
        if (!addToDom) {
            setAddToDom(true);
        }
        if (animationState === SidePanelAnimationState.CLOSING) {
            setAnimation(SidePanelAnimationState.OPENING);
        }
    } else if (animationState !== SidePanelAnimationState.CLOSING) {
        setAnimation(SidePanelAnimationState.CLOSING);
    }

    useIsomorphicLayoutEffect(() => {
        if (isOpen && animationState === SidePanelAnimationState.OPENING) {
            notifyOpening();
        }
    }, [isOpen, animationState, notifyOpening]);

    const handleAnimationEnd = useCallback<React.AnimationEventHandler<HTMLElement>>(
        event => {
            // колбек следит за всеми дочерними элементами, поэтому проверяем, что сработало именно на корневом элементе
            if (event.target !== layerRef.current) {
                return;
            }

            if (!isOpen) {
                setAddToDom(false);
                onAnimationEnd?.('close');
                return;
            }
            if (animationState === SidePanelAnimationState.OPENING) {
                setAnimation(SidePanelAnimationState.NONE);
                onAnimationEnd?.('open');
            }
        },
        [animationState, isOpen, onAnimationEnd],
    );

    const handleMobileAnimationEnd = useCallback(
        (modalAnimationState: ModalAnimationState) => {
            if (modalAnimationState === 'open' || modalAnimationState === 'close') {
                onAnimationEnd?.(modalAnimationState);
            }
        },
        [onAnimationEnd],
    );

    const autoFocusProps = useMemo(
        () => ({
            contain: true,
            restoreFocus: true,
            autoFocus: true,
        }),
        [],
    );

    // Стили самой шторки
    const mainStyle = useMemo<CSSProperties>(
        () => ({
            width: !overlayContent ? `${pixelWidthBySidePanelWidth[width]}px` : undefined,
            zIndex: layerManager.zIndex,
        }),
        [layerManager.zIndex, overlayContent, width],
    );

    // В мобильном режиме превращается в модалку
    if (isMobileMode) {
        return (
            <Modal
                className={className}
                isOpen={isOpen}
                isDraggableMobile={isDraggableMobile}
                onClose={onClose}
                bottomButtons={bottomButtons}
                scrollable
                preventScroll={false}
                data-testid={dataTestId}
                onAnimationEnd={handleMobileAnimationEnd}
            >
                {overlayContent || children}
            </Modal>
        );
    }

    // Имитирует работу компонента Overlay
    if (!addToDom) {
        return null;
    }

    return (
        <LogProvider>
            <OverlayContainer>
                <LayerContextProvider value={layerManager}>
                    <div {...overlayProps} className={cls('overlay')} data-testid={`${dataTestId}.Overlay`}>
                        <LayerFocusScope autoFocusProps={autoFocusProps} isOpen={isOpen}>
                            <div
                                className={cls({ animationState }, [className, colorSkinClass])}
                                style={mainStyle}
                                data-testid={dataTestId}
                                onAnimationEnd={handleAnimationEnd}
                                tabIndex={-1}
                                focus-fallback="true"
                                ref={layerRef}
                            >
                                {overlayContent && (
                                    <>
                                        <FloatingButton
                                            icon={IconCloseSkinny}
                                            size="xs"
                                            color="contrast"
                                            className={cls('closeButton')}
                                            onClick={onClose}
                                            data-testid={`${dataTestId}.CloseButton`}
                                        />
                                        <div className={cls('overlaySlot')}>{overlayContent}</div>
                                    </>
                                )}
                                {!overlayContent && (
                                    <>
                                        <VStack className={cls('container')}>
                                            <Island
                                                ref={setContentContainerRef}
                                                className={cls('contentContainer', { hasBottomButtons, hasScrollbar }, [
                                                    scrollClasses,
                                                ])}
                                                data-testid={`${dataTestId}.ContentContainer`}
                                            >
                                                <div
                                                    className={cls('contentInner', { hasBottomButtons }, [
                                                        contentClassName,
                                                    ])}
                                                    data-testid={`${dataTestId}.ContentInner`}
                                                >
                                                    {children}
                                                </div>
                                            </Island>
                                            {bottomButtons && (
                                                <div
                                                    className={cls('bottomButtons', { hasScrollbar })}
                                                    data-testid={`${dataTestId}.BottomButtons`}
                                                >
                                                    <HStack gap="8" justifyContent="space-between">
                                                        {bottomButtons}
                                                    </HStack>
                                                </div>
                                            )}
                                        </VStack>
                                        <FloatingButton
                                            icon={IconCloseSkinny}
                                            size="xs"
                                            color="contrast"
                                            className={cls('closeButton')}
                                            onClick={onClose}
                                            data-testid={`${dataTestId}.CloseButton`}
                                        />
                                    </>
                                )}
                            </div>
                        </LayerFocusScope>
                    </div>
                </LayerContextProvider>
            </OverlayContainer>
        </LogProvider>
    );
});
